import { useMemo } from 'react';
import {
  AddQuestion,
  type ScorecardItem,
  type ScorecardTemplateBuilderHandle,
} from '@kula-ai/components';

interface ScorecardAddQuestionProps {
  formRef: React.RefObject<ScorecardTemplateBuilderHandle | null>;
  /** Note text rendered alongside the add button. */
  noteText?: React.ReactNode;
}

/**
 * Drop-in replacement for kula's `AddQuestion` that appends instead of
 * prepending.
 *
 * The builder's handle only exposes `prepend`, so a new question landed above
 * the existing ones and the scorecard was saved in the reverse of the order it
 * was written (ENG-3742). kula's `AddQuestion` calls nothing but
 * `formRef.current.prepend`, so proxying that one method keeps its popover and
 * question types untouched.
 */
export const ScorecardAddQuestion = ({
  formRef,
  noteText,
}: ScorecardAddQuestionProps) => {
  const appendRef = useMemo(
    () => ({
      get current() {
        const handle = formRef.current;
        if (!handle) return null;
        return {
          ...handle,
          prepend: (item: ScorecardItem) => {
            const values = handle.getValues();
            const scoreCards = [...(values?.scoreCards ?? []), item];
            handle.reset({ ...values, scoreCards });
            // Appended rows land below the fold, so bring the new one into view.
            // Deferred like kula's own focus — the row is not rendered yet.
            setTimeout(() => {
              const field = document.querySelector<HTMLTextAreaElement>(
                `[name="scoreCards.${scoreCards.length - 1}.question"]`
              );
              field?.scrollIntoView({ block: 'center', behavior: 'smooth' });
              field?.focus();
            }, 0);
          },
        } as ScorecardTemplateBuilderHandle;
      },
    }),
    [formRef]
  );

  return <AddQuestion formRef={appendRef} noteText={noteText} />;
};
